/* eslint-disable consistent-return */
/* eslint-disable eqeqeq */
import { useCallback, useState } from 'react';
import GroupService, { defaultGroup } from '../../services/GroupService';
import useObservable from '../UseObservable';
import { useRouter } from '../../routes';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import EventReporter from 'appEvents';
import { mixpanel } from 'appEvents/providers';
import { LOCAL_SELECTED_GROUP_ID, LOCAL_CENTER_ID} from 'constants/localStorage';
import { findItemById, isTeacher, setLocalStorageItem } from '../../lib/helpers';

const setActiveGroupData = (_activeGroup: any) => {
  setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_GROUP_ID, _activeGroup?.id);
  setLocalStorageItem(KEYS_LOCALSTORAGE.CLASSROOM_NAME, _activeGroup?.name);
  EventReporter.setUserAttributes({ classroom_name: _activeGroup?.name });
  mixpanel.getInstance().register({ classroom_name: _activeGroup?.name });
};

const groupService = GroupService.getInstance();

function useGroups() {
  const { currentParamsRef, addParamsToUrl } = useRouter();
  const { centerId, groupId: paramGroupId } = currentParamsRef.current;
  const [groupList = [], setGroupList] = useObservable(groupService.getGroupList$(), groupService.setGroupList$);
  const [teacherGroupList = []] = useObservable(groupService.getTeacherGroupList$());
  const [activeGroup = defaultGroup] = useObservable(groupService.getActiveGroup$());
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupDetails, setGroupDetails] = useState<any>();
  const areGroups = groupList?.length > 0;
  const areStudentsInActiveGroup = activeGroup.babies?.length > 0;

  const setActiveGroupById = useCallback((_groupId?: number, _groupList?: any, _group?: any) => {
    const groupToSearch = _groupId || paramGroupId || Number(LOCAL_SELECTED_GROUP_ID());
    const foundGroup = findItemById(_groupList || groupList, groupToSearch);
    const groupToSet = foundGroup || groupList?.[0] || _group;
    if (groupToSet) {
      setActiveGroupData(groupToSet);
      groupService.setActiveGroup(groupToSet);
      addParamsToUrl({ groupId: groupToSet.id });
      return groupToSet;
    }
  }, [addParamsToUrl, paramGroupId, groupList]);

  const fetchGroups = useCallback(async (cacheRequest?: boolean) => {
    /* NOTE: if the next expression is true it will save api calls, but if the center change
     * it will show the groups of the last active center. */
    if (cacheRequest) return { groups: groupList, activatedGroup: activeGroup };
    /* This line is needed in those cases where the user change the group and then go to another section.
     * When the user came back to home this will return the last group the user change.
     */
    // if (LOCAL_SELECTED_GROUP_ID() && groupList?.length) return { groups: groupList, activatedGroup: findItemById(groupList, Number(LOCAL_SELECTED_GROUP_ID())) };
    setLoadingGroups(true);
    try {
      if (groupList?.length) {
        setLoadingGroups(false);
        return { groups: groupList, activatedGroup: activeGroup };
      }

      const activeCenterId = centerId || LOCAL_CENTER_ID();
      const requestGroups = isTeacher() ? () => groupService.fetchTeacherGroups(activeCenterId) : groupService.fetchGroups;

      setGroupList([]);
      const groups = await requestGroups();
      console.log('%c❕ FETCHED GROUP LIST', 'background: #685000; color: #ffe796; font-size: 11px', groups);
      setGroupList(groups);
      const activatedGroup = setActiveGroupById(paramGroupId || groups[0].id, groups, groups[0]);

      return { groups, activatedGroup };
    } catch (error) {
      return error;
    } finally {
      setLoadingGroups(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupList, activeGroup, paramGroupId, activeGroup?.id, setActiveGroupById]);

  const fetchGroup = useCallback(async (groupId: number) => {
    if (!groupId) return;
    console.log('%cℹ️ FETCH GROUP', 'background: #006579; color: #c2f5ff; font-size: 11px');
    const { group } = await groupService.fetchGroup(groupId);
    groupService.setActiveGroup(group);
    setLoadingGroups(false);
    return group;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);

  const fetchGroupDetails = async (centerId: number, groupId: number) => {
    if (!groupId || !centerId) return;
    try {
      const { group } = await groupService.fetchGroupDetails(centerId, groupId);
      setGroupDetails(group);
    } catch (error) {
      return error;
    }
  };

  return {
    groupList,
    teacherGroupList,
    activeGroup,
    setActiveGroup: groupService.setActiveGroup,
    setActiveGroupById,
    fetchGroups,
    fetchGroupDetails,
    groupDetails,
    fetchGroup,
    loadingGroups,
    areGroups,
    areStudentsInActiveGroup,
  };
}

export default useGroups;
